/**
 * @file Builds the mutation payloads that move or override investments, files and transactions during a transfer.
 */
import uniq from "lodash.uniq";

import { getInvestmentTierAmount } from "./getInvestedAmount";
import type {
  Investment,
  InvestorFile,
  Transaction,
  TierInvestment,
  CustomAmountsMap,
  TransferInvestment,
} from "../types";

type SourceInvestment = Pick<
  Investment,
  "id" | "investor" | "isSigned" | "investments" | "receiverSourceId" | "isPartiallySigned" | "receiverSourceName"
>;

type InvestmentToMove = Pick<TransferInvestment, "id" | "createdAt" | "investments" | "sourceInvestmentId">;

type InvestmentToOverride = Pick<TransferInvestment, "id" | "investments" | "sourceInvestmentId">;

type FileToMove = Pick<InvestorFile, "id" | "batchId">;

type FileToOverride = Pick<InvestorFile, "id" | "batchId" | "createdAt" | "investorAccountId"> & {
  targetInvestmentId: string;
};

type TransactionToMove = Pick<Transaction, "id">;

type TransactionToOverride = Pick<Transaction, "id" | "createdAt" | "investorAccountId"> & {
  targetInvestmentId: string;
};

type Options = {
  filesToMove: FileToMove[];
  getProjectName: (projectId: string) => string;
  sourceProjectId: string;
  targetProjectId: string;
  filesToOverride: FileToOverride[];
  customAmountsMap: CustomAmountsMap;
  getTargetBatchId: (sourceBatchId: string) => string;
  investmentsToMove: InvestmentToMove[];
  sourceInvestments: SourceInvestment[];
  transactionsToMove: TransactionToMove[];
  investmentsToOverride: InvestmentToOverride[];
  transactionsToOverride: TransactionToOverride[];
};

type MoveInvestmentPayload = {
  mutation: {
    projectId: string;
    projectKey: string;
    investments: TierInvestment[];
    projectName: string;
    totalAmount: number;
    receivedAmount: number;
  };
  investmentId: string;
};

type OverrideInvestmentPayload = {
  mutation: {
    isSigned: boolean;
    investments: TierInvestment[];
    totalAmount: number;
    receivedAmount: number;
    receiverSourceId?: string;
    isPartiallySigned: boolean;
    receiverSourceName?: string;
    investor_hasAccreditation?: boolean;
    investor_accreditationExpiresAt?: string;
  };
  investmentId: string;
};

export type TransferInvestmentsPayload = {
  move: MoveInvestmentPayload[];
  delete: string[];
  override: OverrideInvestmentPayload[];
  sourceProjectId: string;
  targetProjectId: string;
  sourceProjectName: string;
  targetProjectName: string;
};

type TransferTransactionPayload = {
  mutation: {
    projectId: string;
    investmentId?: string;
    secondaryKey?: string;
  };
  transactionId: string;
};

export type TransferTransactionsPayload = {
  update: TransferTransactionPayload[];
  sourceProjectName: string;
  targetProjectName: string;
};

type TransferInvestorFilePayload = {
  fileId: string;
  mutation: {
    batchId: string;
    projectId: string;
    investmentId?: string;
    secondaryKey?: string;
  };
};

export type TransferInvestorFilesPayload = {
  update: TransferInvestorFilePayload[];
  sourceProjectName: string;
  targetProjectName: string;
};

export type Payloads = {
  transferInvestmentsPayload: TransferInvestmentsPayload;
  transferTransactionsPayload: TransferTransactionsPayload;
  transferInvestorFilesPayload: TransferInvestorFilesPayload;
};

/**
 * Builds the transfer payloads for the investments, files and transactions selected to move or be overridden.
 */
const getPayloads = ({
  filesToMove,
  getProjectName,
  sourceProjectId,
  targetProjectId,
  filesToOverride,
  customAmountsMap,
  getTargetBatchId,
  investmentsToMove,
  sourceInvestments,
  transactionsToMove,
  investmentsToOverride,
  transactionsToOverride,
}: Options): Payloads => {
  const sourceProjectName = getProjectName(sourceProjectId);
  const targetProjectName = getProjectName(targetProjectId);

  const moveFilesPayload = filesToMove
    .map(({ id, batchId: sourceBatchId }): TransferInvestorFilePayload =>
      ({
        fileId: id,
        mutation: {
          batchId: getTargetBatchId(sourceBatchId),
          projectId: targetProjectId,
        },
      }),
    );

  const overrideFilesPayload = filesToOverride
    .map(({ id, batchId: sourceBatchId, createdAt, investorAccountId, targetInvestmentId }): TransferInvestorFilePayload =>
      ({
        fileId: id,
        mutation: {
          batchId: getTargetBatchId(sourceBatchId),
          projectId: targetProjectId,
          investmentId: targetInvestmentId,
          secondaryKey: [ "FIL", investorAccountId, targetInvestmentId, createdAt ].join("_"),
        },
      }),
    );

  const transferInvestorFilesPayload: TransferInvestorFilesPayload = {
    update: [
      ...moveFilesPayload,
      ...overrideFilesPayload,
    ],
    sourceProjectName,
    targetProjectName,
  };

  const moveTransactionsPayload = transactionsToMove
    .map((transaction): TransferTransactionPayload =>
      ({
        transactionId: transaction.id,
        mutation: {
          projectId: targetProjectId,
        },
      }),
    );

  const overrideTransactionsPayload = transactionsToOverride
    .map(({ id, createdAt, investorAccountId, targetInvestmentId }): TransferTransactionPayload =>
      ({
        transactionId: id,
        mutation: {
          projectId: targetProjectId,
          secondaryKey: [ "TRA", investorAccountId, targetInvestmentId, createdAt ].join("_"),
          investmentId: targetInvestmentId,
        },
      }),
    );

  const transferTransactionsPayload: TransferTransactionsPayload = {
    update: [
      ...moveTransactionsPayload,
      ...overrideTransactionsPayload,
    ],
    sourceProjectName,
    targetProjectName,
  };

  /**
   * Finds the source investment with the given id.
   */
  const getSourceInvestment = (sourceInvestmentId: string) =>
    sourceInvestments.find(({ id }) => id === sourceInvestmentId);

  /**
   * Returns the tier investments for the given target investment, using its custom amounts, its own
   * investments or the sum with its linked source investment.
   */
  const getInvestments = (investment: InvestmentToOverride): TierInvestment[] => {
    const { id, sourceInvestmentId } = investment;

    const investmentCustomAmounts = customAmountsMap[id];
    const hasCustomAmounts = !!investmentCustomAmounts;

    if (hasCustomAmounts) {
      const {
        investmentId,
        sourceInvestmentId: _sourceInvestmentId,
        ...customInvestments
      } = investmentCustomAmounts;

      const customTierInvestments = Object.entries(customInvestments)
        .map(([ projectTierId, amount ]) => ({ projectTierId, amount: Number(amount) }));

      return customTierInvestments;
    }

    if (!sourceInvestmentId) {
      return investment.investments;
    }

    const targetInvestment = investment;
    const sourceInvestment = getSourceInvestment(sourceInvestmentId);

    const tierIds = uniq(
      [
        ...targetInvestment.investments,
        ...sourceInvestment.investments,
      ].map(({ projectTierId }) => projectTierId),
    ).sort();

    const mergedInvestments = tierIds
      .map((projectTierId) => {
        const targetAmount = getInvestmentTierAmount(projectTierId, targetInvestment) || 0;
        const sourceAmount = getInvestmentTierAmount(projectTierId, sourceInvestment) || 0;
        const amount = targetAmount + sourceAmount;

        return { projectTierId, amount };
      });

    return mergedInvestments;
  };

  /**
   * Sums the tier investments computed for the given investment into its total invested amount.
   */
  const getInvestedAmount = (investment: InvestmentToOverride): number => {
    const investedAmount = getInvestments(investment)
      .map(({ amount }) => amount)
      .reduce((cur, acc) => cur + acc, 0);

    return investedAmount;
  };

  const moveInvestmentsPayload = investmentsToMove
    .map((investment): MoveInvestmentPayload =>
      ({
        investmentId: investment.id,
        mutation: {
          projectId: targetProjectId,
          projectName: getProjectName(targetProjectId),
          projectKey: `${targetProjectId}_${investment.createdAt}`,
          investments: getInvestments(investment),
          totalAmount: getInvestedAmount(investment),
          receivedAmount: getInvestedAmount(investment),
        },
      }),
    );

  const overrideInvestmentsPayload = investmentsToOverride
    .map((investment): OverrideInvestmentPayload => {
      const { sourceInvestmentId } = investment;
      const sourceInvestment = getSourceInvestment(sourceInvestmentId);

      const {
        investor,
        isSigned,
        isPartiallySigned,
        receiverSourceId,
        receiverSourceName,
      } = sourceInvestment;

      const {
        hasAccreditation,
        accreditationExpiresAt,
      } = investor;

      const payload: OverrideInvestmentPayload = {
        investmentId: investment.id,
        mutation: {
          investor_hasAccreditation: hasAccreditation,
          investor_accreditationExpiresAt: accreditationExpiresAt,
          isSigned,
          isPartiallySigned,
          investments: getInvestments(investment),
          totalAmount: getInvestedAmount(investment),
          receivedAmount: getInvestedAmount(investment),
        },
      };

      if (receiverSourceId) {
        payload.mutation.receiverSourceId = receiverSourceId;
        payload.mutation.receiverSourceName = receiverSourceName;
      }

      return payload;
    });

  const deleteInvestmentIds = investmentsToOverride
    .map((investment) => {
      const { sourceInvestmentId } = investment;
      return sourceInvestmentId;
    });

  const transferInvestmentsPayload: TransferInvestmentsPayload = {
    sourceProjectId,
    sourceProjectName,
    targetProjectId,
    targetProjectName,
    move: moveInvestmentsPayload,
    override: overrideInvestmentsPayload,
    delete: deleteInvestmentIds,
  };

  return {
    transferInvestmentsPayload,
    transferTransactionsPayload,
    transferInvestorFilesPayload,
  };
};

export default getPayloads;
